// Status-aware todo sorting and DnD snap-position helpers.
// compareTodos / sortTodos handle both plain (position-only) and smart
// (status-grouped) orderings.

import { between, atEnd, atStart } from './fracidx'
import type { Todo, TodoStatus } from '../api/types'

export const STATUS_RANK: Record<TodoStatus, number> = {
  done: 0,
  todo: 1,
}

export function compareTodos(a: Todo, b: Todo, smartSort: boolean): number {
  if (!smartSort) return a.position - b.position
  const rankDiff = STATUS_RANK[a.status] - STATUS_RANK[b.status]
  if (rankDiff !== 0) return rankDiff
  return a.position - b.position
}

export function sortTodos(todos: Todo[], smartSort: boolean): Todo[] {
  return [...todos].sort((a, b) => compareTodos(a, b, smartSort))
}

/**
 * Compute a fractional position for a dragged item being placed into a
 * smart-sorted column.
 *
 * @param smartSortedCol  Destination column sorted by compareTodos(…, true),
 *                        with the dragged item already removed.
 * @param draggedStatus   Status of the item being dropped.
 * @param beforeId        Id of the item the dragged todo should land BEFORE;
 *                        null means append at end of column.
 */
export function computeSnappedPosition(
  smartSortedCol: Todo[],
  draggedStatus: TodoStatus,
  beforeId: string | null,
): number {
  if (smartSortedCol.length === 0) return atEnd(0)

  // Find [groupStart, groupEnd) — the contiguous run of same-status items.
  const rank = STATUS_RANK[draggedStatus]
  let groupStart = smartSortedCol.length
  let groupEnd = smartSortedCol.length

  for (let i = 0; i < smartSortedCol.length; i++) {
    if (STATUS_RANK[smartSortedCol[i].status] === rank) {
      if (groupStart === smartSortedCol.length) groupStart = i
      groupEnd = i + 1
    }
  }

  // If no same-status items exist, find the insertion point between ranks.
  if (groupStart === smartSortedCol.length) {
    // Insert immediately after the last item whose rank is lower (visually higher).
    let insertAt = 0
    for (let i = 0; i < smartSortedCol.length; i++) {
      if (STATUS_RANK[smartSortedCol[i].status] < rank) {
        insertAt = i + 1
      }
    }
    groupStart = insertAt
    groupEnd = insertAt
  }

  // Compute the intended slot from beforeId.
  let intendedSlot: number
  if (beforeId === null) {
    intendedSlot = smartSortedCol.length
  } else {
    const idx = smartSortedCol.findIndex((t) => t.id === beforeId)
    intendedSlot = idx === -1 ? smartSortedCol.length : idx
  }

  // Clamp to the group's valid range [groupStart, groupEnd].
  const slot = Math.max(groupStart, Math.min(groupEnd, intendedSlot))

  // Compute the fractional position at that slot.
  const prev = slot > 0 ? smartSortedCol[slot - 1] : null
  const next = slot < smartSortedCol.length ? smartSortedCol[slot] : null

  if (prev === null && next === null) return atEnd(0)
  if (prev === null) return atStart(next!.position)
  if (next === null) return atEnd(prev.position)
  return between(prev.position, next.position)
}
